import { useState } from "react";

import NotFoundPage from "components/error/NotFoundPage";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import { getSearchQuery } from "components/SearchInput/helpers";
import useErrorHandle from "hooks/useErrorHandle";
import useURLParams from "hooks/useURLParams";
import TablePagination from "components/Table/Pagination";
import usePagination from "components/Table/Pagination/usePagination";
import { PAGINATION_OPTIONS } from "components/Table/Pagination/constants";

import { DEPLOYMENTS_LIMIT, DEPLOYMENTS_LIMIT_STORAGE_KEY } from "./constants";
import TemplateDeploymentsEmpty from "./Empty";
import FiltersLayout from "./FiltersLayout";
import TemplateDeploymentsPageLayout from "./PageLayout";
import useTemplates from "./hooks/useDeployments";
import DeploymentsTable from "./Table";

type TemplateVersionsProps = {
  id: string;
  excludeTestDeployments?: boolean;
};

const TemplateDeployments = ({ id, excludeTestDeployments }: TemplateVersionsProps) => {
  const urlParams = useURLParams();
  const searchInput = getSearchQuery(urlParams);
  const [itemsCount, setItemsCount] = useState<number>(0);

  const {
    initialLoading,
    error,
    deployments,
    loadDeployments,
    endCursor,
    startCursor,
    noResults,
    isEmpty,
    loadingContent,
    predicates,
    paginationResetKey,
  } = useTemplates({ id, excludeTestDeployments });

  const {
    rowsPerPage,
    setRowsPerPage,
    from,
    to,
    lastPage,
    page,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
  } = usePagination({
    fetchItems: loadDeployments,
    endCursor,
    startCursor,
    visibleItemsCount: deployments.length,
    initialLimit: DEPLOYMENTS_LIMIT,
    allItemsCount: itemsCount,
    storageKey: DEPLOYMENTS_LIMIT_STORAGE_KEY,
    resetKey: paginationResetKey,
    loading: loadingContent,
  });

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (initialLoading) {
    return (
      <TemplateDeploymentsPageLayout>
        <PageLayoutSkeleton />
      </TemplateDeploymentsPageLayout>
    );
  }

  if (noResults) {
    return <NotFoundPage />;
  }

  const hasNoResultsWithFiltering = searchInput.length > 0 || predicates.length > 0;

  return (
    <TemplateDeploymentsPageLayout>
      <FiltersLayout
        predicates={predicates}
        itemsCount={itemsCount}
        setItemsCount={setItemsCount}
        id={id}
      >
        {isEmpty && <TemplateDeploymentsEmpty hasNoResults={hasNoResultsWithFiltering} />}

        {deployments.length > 0 && (
          <>
            <DeploymentsTable
              deployments={deployments}
              loading={loadingContent}
              rowsPerPage={rowsPerPage}
            />

            <TablePagination
              from={from}
              to={to}
              loading={loadingContent}
              goToNextPage={goToNextPage}
              setRowsPerPage={setRowsPerPage}
              goToPrevPage={goToPrevPage}
              goToFirstPage={goToFirstPage}
              goToLastPage={goToLastPage}
              rowsPerPage={rowsPerPage}
              itemsCount={itemsCount}
              lastPage={lastPage}
              page={page}
              paginationOptions={PAGINATION_OPTIONS}
            />
          </>
        )}
      </FiltersLayout>
    </TemplateDeploymentsPageLayout>
  );
};

export default TemplateDeployments;
